import { keepPreviousData, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, fetchJson } from "@/lib/queryClient";
import type { PainelInicioDTO } from "@shared/mercado-inicio";
import type {
  CarteiraDTO,
  FiltroCarteira,
  APagarDTO,
  FiltroAPagar,
  ItemLotePagamento,
  ResultadoLoteDTO,
  AReceberDTO,
  CargaDetalheDTO,
  CargaLinhaDTO,
  CargaPatch,
  ChamadaDTO,
  DadosPagamento,
  DestinoDTO,
  InicioCompradorDTO,
  ItemRegistrado,
  NovaCarga,
  NovoItemRegistrado,
  ProdutorCarteiraDTO,
  QuadroCargasDTO,
  ResolucaoProdutor,
  RomaneioDisponivelDTO,
} from "@shared/mercado-comprador";

/**
 * Mercado C5a — a CARGA e a carteira do comprador.
 *
 * ⚠️ NENHUM CÁLCULO AQUI. Custo, kg líquido, margem e faltam vêm prontos do
 * servidor dentro de `resumo`; a tela só formata. Recontar no client criaria a
 * segunda verdade sobre a mesma carga — que é exatamente o que o teste de
 * contrato (função ≡ rota) existe para impedir.
 */

const CARGAS = "/api/mercado/comprador/cargas";
const PRODUTORES = "/api/mercado/comprador/produtores";
const DESTINOS = "/api/mercado/comprador/destinos";
const PAGAR = "/api/mercado/comprador/pagar";
const RECEBER = "/api/mercado/comprador/receber";
const INICIO = "/api/mercado/comprador/inicio";
const ROMANEIOS = "/api/mercado/comprador/romaneios";

/**
 * Uma carga muda custo, carteira e disponíveis de uma vez — invalidar junto.
 *
 * O Início entra desde o C8b: ele resume as cargas em andamento e o que falta
 * para o alvo, e uma home que discorda da lista que resume é a forma mais rápida
 * de perder a confiança na tela inteira.
 */
function invalidarCargas(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: [CARGAS] });
  qc.invalidateQueries({ queryKey: [PRODUTORES] });
  qc.invalidateQueries({ queryKey: [INICIO] });
}

/**
 * A lista CHAPADA — a que o Início e o seletor de carga do Romaneios leem.
 *
 * ⚠️ Continua existindo ao lado do quadro porque são perguntas diferentes: aqui
 * é "quais cargas existem neste estado", lá é "o que está andando, em três
 * colunas, com os quatro números". Uma consulta só devolveria o quadro inteiro
 * para quem só quer preencher um `<select>`.
 */
export function useCargas(estado: string) {
  return useQuery<CargaLinhaDTO[]>({
    queryKey: [CARGAS, "lista", estado],
    queryFn: () => fetchJson<CargaLinhaDTO[]>(`${CARGAS}?estado=${encodeURIComponent(estado)}`),
  });
}

/**
 * O QUADRO — três colunas e os quatro números, numa chamada.
 *
 * ⚠️ UMA consulta para a tela inteira. Quatro tiles + três colunas em
 * requisições separadas seriam quatro esqueletos piscando em sequência no
 * celular, e quatro chances de a tela mostrar números de instantes diferentes.
 */
export function useQuadroDeCargas(
  filtro: { periodo?: string; de?: string; ate?: string; cliente?: string; q?: string },
  ativo: boolean,
) {
  const p = new URLSearchParams();
  if (filtro.periodo && filtro.periodo !== "mes") p.set("periodo", filtro.periodo);
  if (filtro.de) p.set("de", filtro.de);
  if (filtro.ate) p.set("ate", filtro.ate);
  if (filtro.cliente && filtro.cliente !== "todos") p.set("cliente", filtro.cliente);
  if (filtro.q) p.set("q", filtro.q);
  const qs = p.toString();
  return useQuery<QuadroCargasDTO>({
    queryKey: [CARGAS, "quadro", qs],
    queryFn: () => fetchJson<QuadroCargasDTO>(`${CARGAS}${qs ? `?${qs}` : ""}`),
    enabled: ativo,
  });
}

/** D5 — o que aparece no documento desta carga. */
export function useConfigDocumento(cargaId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { mostrarProdutores?: boolean; mostrarBruto?: boolean }) =>
      apiRequest("PATCH", `${CARGAS}/${cargaId}/documento`, dados)
        .then(r => r.json()) as Promise<CargaDetalheDTO>,
    onSuccess: () => invalidarCargas(qc),
  });
}

/**
 * "Enviei ao cliente" — grava o evento ANTES de abrir o WhatsApp.
 *
 * ⚠️ O app não sabe se a mensagem saiu (quem envia é o corretor, no WhatsApp
 * dele). O evento registra a INTENÇÃO, e é ele que vira o selo "Documento
 * enviado" no cartão do quadro — a pendência que mais escapa.
 */
export function useMarcarDocumentoEnviado(cargaId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest("POST", `${CARGAS}/${cargaId}/documento/enviado`)
      .then(r => r.json()) as Promise<CargaDetalheDTO>,
    onSuccess: () => invalidarCargas(qc),
  });
}

export function useCarga(id: string | undefined) {
  return useQuery<CargaDetalheDTO>({
    queryKey: [CARGAS, id],
    queryFn: () => fetchJson<CargaDetalheDTO>(`${CARGAS}/${id}`),
    enabled: !!id,
  });
}

/** Romaneios formalizados para este comprador que não estão em carga nenhuma. */
export function useRomaneiosDisponiveis(ativo: boolean) {
  return useQuery<RomaneioDisponivelDTO[]>({
    queryKey: [CARGAS, "romaneios-disponiveis"],
    queryFn: () => fetchJson<RomaneioDisponivelDTO[]>(`${CARGAS}/romaneios-disponiveis`),
    enabled: ativo,
  });
}

/** Só busca quando o painel "Chamar produtores" está aberto. */
export function useChamada(cargaId: string | undefined, ativo: boolean) {
  return useQuery<ChamadaDTO>({
    queryKey: [CARGAS, cargaId, "chamada"],
    queryFn: () => fetchJson<ChamadaDTO>(`${CARGAS}/${cargaId}/chamada`),
    enabled: ativo && !!cargaId,
  });
}

export function useCriarCarga() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: NovaCarga) =>
      apiRequest("POST", CARGAS, dados).then(r => r.json()) as Promise<CargaDetalheDTO>,
    onSuccess: () => invalidarCargas(qc),
  });
}

export function useAtualizarCarga(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: CargaPatch) =>
      apiRequest("PATCH", `${CARGAS}/${id}`, dados).then(r => r.json()) as Promise<CargaDetalheDTO>,
    onSuccess: () => invalidarCargas(qc),
  });
}

export function useAdicionarRomaneio(cargaId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (compartilhamentoId: string) =>
      apiRequest("POST", `${CARGAS}/${cargaId}/romaneios`, { compartilhamentoId }).then(r => r.json()),
    onSuccess: () => invalidarCargas(qc),
  });
}

/**
 * O resultado de registrar um produtor: gravou, OU há contato parecido e a tela
 * precisa PERGUNTAR.
 *
 * ⚠️ Este é o único lugar do módulo que não usa `apiRequest`, e é de propósito:
 * `throwIfResNotOk` transforma o 409 numa `Error` com o JSON dentro da string, e
 * a tela precisa do `sugestao` PARSEADO para montar o diálogo "É ele / É outro".
 * Fazer regex sobre mensagem de erro para recuperar dado estruturado é como se
 * perde a sugestão no primeiro campo novo.
 */
export type ResultadoRegistro =
  | { ok: true; carga: CargaDetalheDTO }
  | { ok: false; parecidos: ProdutorCarteiraDTO[]; mensagem: string };

export function useRegistrarItem(cargaId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<ResultadoRegistro, Error, NovoItemRegistrado>({
    mutationFn: async (dados) => {
      const res = await fetch(`${CARGAS}/${cargaId}/itens`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dados),
        credentials: "include",
      });
      if (res.ok) return { ok: true, carga: (await res.json()) as CargaDetalheDTO };

      const corpo = await res.json().catch(() => null) as
        { code?: string; error?: string; sugestao?: ProdutorCarteiraDTO[] } | null;
      if (res.status === 409 && corpo?.code === "PRODUTOR_PARECIDO") {
        return { ok: false, parecidos: corpo.sugestao ?? [], mensagem: corpo.error ?? "" };
      }
      // Qualquer outra falha é erro de verdade e sobe — nunca engolida num
      // `catch {}`, que deixaria o comprador achando que o item foi gravado.
      throw new Error(corpo?.error ?? `${res.status}: não consegui registrar o produtor.`);
    },
    onSuccess: (r) => { if (r.ok) invalidarCargas(qc); },
  });
}

export function useEditarItem(cargaId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { itemId: string; itens?: ItemRegistrado[]; vencimento?: string | null }) =>
      apiRequest("PATCH", `${CARGAS}/${cargaId}/itens/${dados.itemId}`, {
        itens: dados.itens,
        vencimento: dados.vencimento,
      }).then(r => r.json()) as Promise<CargaDetalheDTO>,
    onSuccess: () => invalidarCargas(qc),
  });
}

export function useRemoverItem(cargaId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      apiRequest("DELETE", `${CARGAS}/${cargaId}/itens/${itemId}`).then(r => r.json()) as Promise<CargaDetalheDTO>,
    onSuccess: () => invalidarCargas(qc),
  });
}

export function useRegistrarDespesa(cargaId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { tipo: string; descricao?: string | null; valor: number }) =>
      apiRequest("POST", `${CARGAS}/${cargaId}/despesas`, dados).then(r => r.json()) as Promise<CargaDetalheDTO>,
    onSuccess: () => invalidarCargas(qc),
  });
}

export function useRemoverDespesa(cargaId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (despesaId: string) =>
      apiRequest("DELETE", `${CARGAS}/${cargaId}/despesas/${despesaId}`).then(r => r.json()) as Promise<CargaDetalheDTO>,
    onSuccess: () => invalidarCargas(qc),
  });
}

export function useVencimentoVenda(cargaId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: string | null) =>
      apiRequest("PATCH", `${CARGAS}/${cargaId}/vencimento-venda`, { data }).then(r => r.json()) as Promise<CargaDetalheDTO>,
    onSuccess: () => invalidarCargas(qc),
  });
}

/** Fechar · reabrir · marcar entregue — uma mutação, o ato no parâmetro. */
export function useEstadoDaCarga(cargaId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { acao: "fechar" | "reabrir" | "entregue"; kgBruto?: number | null; entregueEm?: string | null }) =>
      apiRequest("POST", `${CARGAS}/${cargaId}/${dados.acao}`, {
        kgBruto: dados.kgBruto,
        entregueEm: dados.entregueEm,
      }).then(r => r.json()) as Promise<CargaDetalheDTO>,
    onSuccess: () => invalidarCargas(qc),
  });
}

// ─── Carteira ────────────────────────────────────────────────────────────────

/**
 * O PAINEL da carteira (Produtores v2). O filtro vira query string com as MESMAS
 * chaves da URL da tela — `produtor` é o recorte de um só (a faixa de Romaneios).
 *
 * ⚠️ A chave de cache começa em `[PRODUTORES]`: tudo que já invalida a carteira
 * (registrar romaneio, pagar) invalida o painel junto, sem uma segunda lista.
 */
export function useCarteiraPainel(filtro: FiltroCarteira, ativo = true) {
  const p = new URLSearchParams();
  if (filtro.periodo && filtro.periodo !== "30d") p.set("periodo", filtro.periodo);
  if (filtro.situacao) p.set("situacao", filtro.situacao);
  if (filtro.origem) p.set("origem", filtro.origem);
  if (filtro.q) p.set("q", filtro.q);
  if (filtro.ordem && filtro.ordem !== "aberto") p.set("ordem", filtro.ordem);
  if (filtro.contatoId) p.set("produtor", filtro.contatoId);
  const qs = p.toString();
  return useQuery<CarteiraDTO>({
    queryKey: [PRODUTORES, "painel", qs],
    queryFn: () => fetchJson<CarteiraDTO>(`${PRODUTORES}/painel${qs ? `?${qs}` : ""}`),
    enabled: ativo,
  });
}

export function useProdutoresCarteira(ativo: boolean) {
  return useQuery<ProdutorCarteiraDTO[]>({
    queryKey: [PRODUTORES],
    queryFn: () => fetchJson<ProdutorCarteiraDTO[]>(PRODUTORES),
    enabled: ativo,
  });
}

/**
 * O autocomplete do registro. `enabled` a partir de 2 caracteres: com 1, a
 * resposta é a carteira inteira e o "parecidos" perde o sentido.
 */
export function useResolverProdutor(q: string) {
  return useQuery<ResolucaoProdutor>({
    queryKey: [PRODUTORES, "resolver", q],
    queryFn: () => fetchJson<ResolucaoProdutor>(`${PRODUTORES}/resolver?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length >= 2,
  });
}

export function useSalvarProdutor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { id?: string; nome: string; apelidos?: string[]; telefone?: string | null; cidade?: string | null }) =>
      (dados.id
        ? apiRequest("PATCH", `${PRODUTORES}/${dados.id}`, dados)
        : apiRequest("POST", PRODUTORES, dados)
      ).then(r => r.json()) as Promise<ProdutorCarteiraDTO>,
    onSuccess: () => invalidarCargas(qc),
  });
}

export function useDestinos(ativo: boolean) {
  return useQuery<DestinoDTO[]>({
    queryKey: [DESTINOS],
    queryFn: () => fetchJson<DestinoDTO[]>(DESTINOS),
    enabled: ativo,
  });
}

export function useSalvarDestino() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { id?: string; nome: string; cidade?: string | null; telefone?: string | null; kgCaixa?: number | null }) =>
      (dados.id
        ? apiRequest("PATCH", `${DESTINOS}/${dados.id}`, dados)
        : apiRequest("POST", DESTINOS, dados)
      ).then(r => r.json()) as Promise<DestinoDTO>,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: [DESTINOS] });
      qc.invalidateQueries({ queryKey: [CARGAS] });
      return d;
    },
  });
}

// ─── C5b: a carga como documento ─────────────────────────────────────────────

export interface MensagemEnvioDTO {
  texto: string;
  url: string;
  /** `null` quando o contato não tem WhatsApp — a tela oferece só "Copiar". */
  link: string | null;
  destinoNome?: string | null;
  produtorNome?: string | null;
}

/** O texto + o `wa.me` do DESTINO. Só busca com o painel aberto. */
export function useMensagemDaCarga(cargaId: string | undefined, ativo: boolean) {
  return useQuery<MensagemEnvioDTO>({
    queryKey: [CARGAS, cargaId, "mensagem"],
    queryFn: () => fetchJson<MensagemEnvioDTO>(`${CARGAS}/${cargaId}/mensagem`),
    enabled: ativo && !!cargaId,
  });
}

/** O texto + o `wa.me` do PRODUTOR de um registro próprio. */
export function useMensagemDoItem(cargaId: string | undefined, itemId: string | undefined, ativo: boolean) {
  return useQuery<MensagemEnvioDTO>({
    queryKey: [CARGAS, cargaId, "itens", itemId, "mensagem"],
    queryFn: () => fetchJson<MensagemEnvioDTO>(`${CARGAS}/${cargaId}/itens/${itemId}/mensagem`),
    enabled: ativo && !!cargaId && !!itemId,
  });
}

/**
 * "Marquei como enviado" — evento, não estado derivado. O app não sabe se a
 * mensagem saiu de verdade (quem envia é o comprador, no WhatsApp dele).
 */
export function useMarcarLinkEnviado(cargaId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { itemId?: string; alvo?: string }) =>
      apiRequest("POST", `${CARGAS}/${cargaId}/link-enviado`, dados).then(r => r.json()),
    onSuccess: () => invalidarCargas(qc),
  });
}

const PAGAMENTO = "/api/mercado/comprador/dados-pagamento";

/** Onde o dinheiro cai — cadastro do comprador, copiado para a carga ao fechar. */
export function useDadosPagamento(ativo: boolean) {
  return useQuery<DadosPagamento>({
    queryKey: [PAGAMENTO],
    queryFn: () => fetchJson<DadosPagamento>(PAGAMENTO),
    enabled: ativo,
  });
}

export function useSalvarDadosPagamento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: DadosPagamento) =>
      apiRequest("PUT", PAGAMENTO, dados).then(r => r.json()) as Promise<DadosPagamento>,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PAGAMENTO] });
      // O snapshot da carga só é tirado ao FECHAR — mas a tela mostra o cadastro
      // vivo no card Venda, e ele acabou de mudar.
      qc.invalidateQueries({ queryKey: [CARGAS] });
    },
  });
}

// ─── C4: o dinheiro ──────────────────────────────────────────────────────────

/**
 * "A pagar", com os filtros da tela. KPIs, chips, grupos e subtotais vêm do
 * servidor já com os filtros aplicados (os KPIs, por regra, sem eles).
 * `keepPreviousData` segura a tabela na tela enquanto o próximo filtro chega.
 */
export function useAPagar(filtro: FiltroAPagar = {}, ativo = true) {
  const p = new URLSearchParams();
  if (filtro.situacao && filtro.situacao !== "ABERTO") p.set("situacao", filtro.situacao);
  if (filtro.produtorContatoId) p.set("produtor", filtro.produtorContatoId);
  if (filtro.origem && filtro.origem !== "todas") p.set("origem", filtro.origem);
  if (filtro.agrupar && filtro.agrupar !== "vencimento") p.set("agrupar", filtro.agrupar);
  if (filtro.q) p.set("q", filtro.q);
  if (filtro.pagosDe) p.set("pagosDe", filtro.pagosDe);
  if (filtro.pagosAte) p.set("pagosAte", filtro.pagosAte);
  const qs = p.toString();
  return useQuery<APagarDTO>({
    queryKey: [PAGAR, qs],
    queryFn: () => fetchJson<APagarDTO>(qs ? `${PAGAR}?${qs}` : PAGAR),
    enabled: ativo,
    placeholderData: keepPreviousData,
  });
}

/** Vários documentos num lançamento cada, numa transação só. */
export function useInformarPagamentoEmLote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { itens: ItemLotePagamento[]; data: string; forma: string; observacao?: string | null }) =>
      apiRequest("POST", `${PAGAR}/informar-lote`, dados).then(r => r.json() as Promise<ResultadoLoteDTO>),
    onSuccess: () => invalidarDinheiro(qc),
  });
}

/** Informar muda a lista, a carga e a carteira de uma vez. */
function invalidarDinheiro(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: [PAGAR] });
  qc.invalidateQueries({ queryKey: [CARGAS] });
  qc.invalidateQueries({ queryKey: [PRODUTORES] });
  qc.invalidateQueries({ queryKey: [INICIO] });
  // ⚠️ ROMANEIOS entra desde que o painel do romaneio passou a informar
  // pagamento: sem isto o saldo que o corretor acabou de baixar continuaria na
  // tela que ele está olhando, e ele lançaria de novo.
  qc.invalidateQueries({ queryKey: [ROMANEIOS] });
}

export function useInformarPagamento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: {
      compartilhamentoId?: string;
      cargaItemId?: string;
      valor?: number | null;
      data: string;
      forma: string;
    }) => apiRequest("POST", `${PAGAR}/informar`, dados).then(r => r.json()),
    onSuccess: () => invalidarDinheiro(qc),
  });
}

/** Um lançamento por item com saldo — nunca um agregado. */
export function useInformarPagamentoDaCarga(cargaId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { data: string; forma: string }) =>
      apiRequest("POST", `${PAGAR}/carga/${cargaId}`, dados)
        .then(r => r.json()) as Promise<{ lancados: number; total: number }>,
    onSuccess: () => invalidarDinheiro(qc),
  });
}

// ─── A receber e o Início (C8b) ──────────────────────────────────────────────

/**
 * O que os destinos devem ao corretor.
 *
 * ⚠️ SÓ O CORRETOR tem esta tela — a rota responde 403 `PERFIL_INVALIDO` ao
 * distribuidor. Quem chama passa `ativo` já sabendo o perfil, para não disparar
 * um request que nasce recusado.
 */
export function useAReceber(ativo: boolean) {
  return useQuery<AReceberDTO>({
    queryKey: [RECEBER],
    queryFn: () => fetchJson<AReceberDTO>(RECEBER),
    enabled: ativo,
  });
}

/**
 * Um recebimento muda a lista, o Início e a carga — e, quando quem informa é o
 * DISTRIBUIDOR, muda também o "A pagar" dele (é a linha `CARGA`).
 */
function invalidarRecebimento(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: [RECEBER] });
  qc.invalidateQueries({ queryKey: [INICIO] });
  qc.invalidateQueries({ queryKey: [PAGAR] });
  qc.invalidateQueries({ queryKey: [CARGAS] });
}

export function useInformarRecebimento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { cargaId: string; valor?: number | null; data: string; forma: string }) =>
      apiRequest("POST", `${RECEBER}/${dados.cargaId}/informar`, {
        valor: dados.valor, data: dados.data, forma: dados.forma,
      }).then(r => r.json()),
    onSuccess: () => invalidarRecebimento(qc),
  });
}

/** Confirmar e contestar são do CORRETOR que formou a carga (regra 5). */
export function useConfirmarRecebimento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lancamentoId: string) =>
      apiRequest("POST", `${RECEBER}/lancamentos/${lancamentoId}/confirmar`).then(r => r.json()),
    onSuccess: () => invalidarRecebimento(qc),
  });
}

export function useContestarRecebimento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { lancamentoId: string; motivo: string }) =>
      apiRequest("POST", `${RECEBER}/lancamentos/${dados.lancamentoId}/contestar`, { motivo: dados.motivo })
        .then(r => r.json()),
    onSuccess: () => invalidarRecebimento(qc),
  });
}

/** O Início inteiro numa chamada — ver `montarInicio`. Serve aos dois perfis. */
export function useInicioComprador(ativo: boolean) {
  return useQuery<PainelInicioDTO>({
    queryKey: [INICIO],
    queryFn: () => fetchJson<PainelInicioDTO>(INICIO),
    enabled: ativo,
  });
}

